import { useEffect, useState } from 'react';
import { activePreset, dubaiToday, MAX_RANGE_DAYS, PRESET_DAYS, presetRange, RangeError, validateRange } from './marketingMath';

interface Props {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
}

const ERRORS: Record<RangeError, string> = {
  invalid: 'Pick both dates.',
  order: 'The start date must be on or before the end date.',
  'too-long': `Pick a range of ${MAX_RANGE_DAYS} days or fewer.`,
};

/** Spec 078 — 7 / 14 / 30 days (ending today, Dubai) or a custom range of at most 92 days. */
export default function DateRangeSelect({ from, to, onChange }: Props) {
  const today = dubaiToday();
  const preset = activePreset(from, to, today);
  const [customOpen, setCustomOpen] = useState(preset === null);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  useEffect(() => { setDraftFrom(from); setDraftTo(to); }, [from, to]);
  const error = validateRange(draftFrom, draftTo);

  return (
    <div className="mkt-range">
      <div className="dash-toggle" role="group" aria-label="Date range">
        {PRESET_DAYS.map((d) => (
          <button key={d} type="button" className={`dash-toggle__btn${preset === d && !customOpen ? ' dash-toggle__btn--active' : ''}`}
            aria-pressed={preset === d && !customOpen} onClick={() => { setCustomOpen(false); onChange(presetRange(d, today)); }}>
            {d} days
          </button>
        ))}
        <button type="button" className={`dash-toggle__btn${customOpen ? ' dash-toggle__btn--active' : ''}`} aria-pressed={customOpen}
          aria-expanded={customOpen} onClick={() => setCustomOpen(true)}>
          Custom
        </button>
      </div>
      {customOpen && (
        <form className="mkt-custom" onSubmit={(e) => { e.preventDefault(); if (!error) onChange({ from: draftFrom, to: draftTo }); }}>
          <label className="mkt-field">From
            <input type="date" value={draftFrom} max={today} onChange={(e) => setDraftFrom(e.target.value)} aria-invalid={!!error} />
          </label>
          <label className="mkt-field">To
            <input type="date" value={draftTo} max={today} onChange={(e) => setDraftTo(e.target.value)} aria-invalid={!!error} />
          </label>
          <button type="submit" className="admin-btn admin-btn-primary admin-btn-sm" disabled={!!error}>Apply</button>
          {error && <span className="mkt-field-error" role="alert">{ERRORS[error]}</span>}
        </form>
      )}
    </div>
  );
}
